/**
 * Kullanıcı giriş kısıtı: member kullanıcının panele hangi gün/saatlerde ve hangi
 * IP adreslerinden girebileceği (tenant_users.access_* kolonları).
 *
 * Saf TS: middleware (Edge runtime, node:net yok), server action ve istemci aynı
 * modülü kullanır. Owner/Admin hiçbir zaman kısıtlanmaz; karar çağıranda rolle verilir.
 * Saatler Türkiye saatidir (Europe/Istanbul, yaz saati yok).
 */

export type AccessPolicy = {
  /** ISO hafta günleri (1=Pzt … 7=Paz); null = gün/saat kısıtı yok. */
  days: number[] | null
  /** 'HH:MM'; ikisi birlikte null = seçili günlerde tüm gün. Bitiş < başlangıç = gece vardiyası. */
  timeStart: string | null
  timeEnd: string | null
  /** IPv4/IPv6 adres veya CIDR; null = IP kısıtı yok. */
  ips: string[] | null
}

export type AccessPolicyRow = {
  access_days?: number[] | null
  access_time_start?: string | null
  access_time_end?: string | null
  access_allowed_ips?: string[] | null
}

export type AccessDenial = 'erisim-saat' | 'erisim-ip'

export const NO_ACCESS_POLICY: AccessPolicy = { days: null, timeStart: null, timeEnd: null, ips: null }
export const ACCESS_TIME_ZONE = 'Europe/Istanbul'
export const MAX_ACCESS_IPS = 50
export const DAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'] as const

export const ACCESS_DENIAL_MESSAGES: Record<AccessDenial, string> = {
  'erisim-saat': 'Hesabınız için tanımlı erişim saatleri dışındasınız. Panele yalnızca yöneticinizin belirlediği gün ve saatlerde girebilirsiniz.',
  'erisim-ip': 'Bulunduğunuz ağdan panele erişim izniniz yok. Yalnızca yöneticinizin izin verdiği IP adreslerinden giriş yapabilirsiniz.',
}

export function isAccessDenial(value: unknown): value is AccessDenial {
  return value === 'erisim-saat' || value === 'erisim-ip'
}

// ─── IP ayrıştırma ──────────────────────────────────────────────────────────

type ParsedIp = { version: 4 | 6; bytes: number[] }
type ParsedCidr = ParsedIp & { prefix: number }

function parseIpv4(input: string): number[] | null {
  const parts = input.split('.')
  if (parts.length !== 4) return null
  const bytes: number[] = []
  for (const part of parts) {
    // Baştaki sıfır (010) sekizlik/ondalık belirsizliği yaratır; reddedilir.
    if (!/^(0|[1-9]\d{0,2})$/.test(part)) return null
    const n = Number(part)
    if (n > 255) return null
    bytes.push(n)
  }
  return bytes
}

function parseIpv6(input: string): number[] | null {
  if (!/^[0-9a-fA-F:.]+$/.test(input)) return null
  const halves = input.split('::')
  if (halves.length > 2) return null

  const groupsOf = (half: string): number[] | null => {
    if (half === '') return []
    const groups: number[] = []
    const parts = half.split(':')
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]!
      if (part.includes('.')) {
        // Gömülü IPv4 yalnız son parçada olabilir (::ffff:1.2.3.4).
        if (i !== parts.length - 1) return null
        const v4 = parseIpv4(part)
        if (!v4) return null
        groups.push((v4[0]! << 8) | v4[1]!, (v4[2]! << 8) | v4[3]!)
      } else {
        if (!/^[0-9a-fA-F]{1,4}$/.test(part)) return null
        groups.push(parseInt(part, 16))
      }
    }
    return groups
  }

  const head = groupsOf(halves[0]!)
  const tail = halves.length === 2 ? groupsOf(halves[1]!) : []
  if (!head || !tail) return null
  const missing = 8 - head.length - tail.length
  if (halves.length === 2 ? missing < 1 : missing !== 0) return null
  const groups = [...head, ...new Array<number>(missing).fill(0), ...tail]
  return groups.flatMap((g) => [g >> 8, g & 0xff])
}

/** IPv4-mapped IPv6 (::ffff:a.b.c.d) IPv4 olarak döner; aynı istemci tek biçimde eşleşir. */
export function parseIp(input: string): ParsedIp | null {
  const value = input.trim()
  if (!value) return null
  if (!value.includes(':')) {
    const bytes = parseIpv4(value)
    return bytes ? { version: 4, bytes } : null
  }
  const bytes = parseIpv6(value)
  if (!bytes) return null
  const mapped = bytes.slice(0, 10).every((b) => b === 0) && bytes[10] === 0xff && bytes[11] === 0xff
  return mapped ? { version: 4, bytes: bytes.slice(12) } : { version: 6, bytes }
}

function parseCidr(input: string): ParsedCidr | null {
  const [address, prefixText, extra] = input.trim().split('/')
  if (extra !== undefined || address === undefined) return null
  const ip = parseIp(address)
  if (!ip) return null
  const max = ip.version === 4 ? 32 : 128
  if (prefixText === undefined) return { ...ip, prefix: max }
  if (!/^\d{1,3}$/.test(prefixText)) return null
  // ::ffff:a.b.c.d/120 gibi mapped CIDR IPv4 önekine çevrilir.
  const mappedInput = ip.version === 4 && address.includes(':')
  const prefix = Number(prefixText) - (mappedInput ? 96 : 0)
  if (prefix < 0 || prefix > max) return null
  return { ...ip, prefix }
}

function maskBytes(bytes: number[], prefix: number): number[] {
  return bytes.map((b, i) => {
    const bits = Math.max(0, Math.min(8, prefix - i * 8))
    return bits === 8 ? b : b & ((0xff << (8 - bits)) & 0xff)
  })
}

function formatIpv6(bytes: number[]): string {
  const groups = Array.from({ length: 8 }, (_, i) => (bytes[i * 2]! << 8) | bytes[i * 2 + 1]!)
  // RFC 5952: en uzun (≥2) sıfır dizisi '::' olur.
  let bestStart = -1
  let bestLen = 0
  for (let i = 0; i < 8;) {
    if (groups[i] !== 0) { i++; continue }
    let j = i
    while (j < 8 && groups[j] === 0) j++
    if (j - i > bestLen && j - i >= 2) { bestStart = i; bestLen = j - i }
    i = j
  }
  const hex = groups.map((g) => g.toString(16))
  if (bestStart < 0) return hex.join(':')
  return `${hex.slice(0, bestStart).join(':')}::${hex.slice(bestStart + bestLen).join(':')}`
}

function formatIp(ip: ParsedIp): string {
  return ip.version === 4 ? ip.bytes.join('.') : formatIpv6(ip.bytes)
}

/**
 * IP/CIDR girdisini kanonik biçime çevirir; geçersizse null.
 * Tek adres öneksiz yazılır, ağdaki host bitleri sıfırlanır (10.0.0.5/24 → 10.0.0.0/24).
 */
export function normalizeIpEntry(input: string): string | null {
  const cidr = parseCidr(input)
  if (!cidr) return null
  const max = cidr.version === 4 ? 32 : 128
  const network = formatIp({ version: cidr.version, bytes: maskBytes(cidr.bytes, cidr.prefix) })
  return cidr.prefix === max ? network : `${network}/${cidr.prefix}`
}

/** İstemci IP'si listedeki adres/ağlardan birine düşüyor mu? Geçersiz IP hiçbir zaman eşleşmez. */
export function ipMatchesAny(ip: string, entries: readonly string[]): boolean {
  const client = parseIp(ip)
  if (!client) return false
  return entries.some((entry) => {
    const cidr = parseCidr(entry)
    if (!cidr || cidr.version !== client.version) return false
    const a = maskBytes(client.bytes, cidr.prefix)
    const b = maskBytes(cidr.bytes, cidr.prefix)
    return a.every((byte, i) => byte === b[i])
  })
}

/** "Mevcut IP'mi ekle" önerisi: IPv6 adresi gizlilik uzantısıyla değiştiğinden /64 ağı önerilir. */
export function suggestedIpEntry(ip: string): string | null {
  const parsed = parseIp(ip)
  if (!parsed) return null
  return parsed.version === 4 ? formatIp(parsed) : normalizeIpEntry(`${formatIp(parsed)}/64`)
}

// ─── Gün / saat ─────────────────────────────────────────────────────────────

const WEEKDAY_ISO: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }
const CLOCK_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: ACCESS_TIME_ZONE,
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

/** Türkiye saatinde ISO gün (1–7) ve gün içi dakika (0–1439). */
export function accessClock(now: Date): { day: number; minutes: number } {
  const parts = Object.fromEntries(CLOCK_FORMAT.formatToParts(now).map((p) => [p.type, p.value]))
  return { day: WEEKDAY_ISO[parts.weekday ?? ''] ?? 0, minutes: Number(parts.hour) * 60 + Number(parts.minute) }
}

/** 'HH:MM' veya DB'nin 'HH:MM:SS' biçimi → dakika; geçersizse null. */
export function timeToMinutes(value: string | null | undefined): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d(?:\.\d+)?)?$/.exec(value ?? '')
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

function minutesToTime(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

/**
 * Gün/saat penceresi içinde mi? Başlangıç dahil, bitiş hariç (08:00–18:00 → 18:00'de kapanır).
 * Gece vardiyasında (22:00–06:00) gece yarısından sonraki kısım başladığı güne sayılır:
 * Pzt seçiliyse Salı 03:00 açıktır. Bozuk veri kapalı sayılır.
 */
export function isWithinSchedule(policy: AccessPolicy, now: Date): boolean {
  if (policy.days === null) return true
  const { day, minutes } = accessClock(now)
  const previousDay = day === 1 ? 7 : day - 1
  if (policy.timeStart === null && policy.timeEnd === null) return policy.days.includes(day)
  const start = timeToMinutes(policy.timeStart)
  const end = timeToMinutes(policy.timeEnd)
  if (start === null || end === null || start === end) return false
  if (start < end) return policy.days.includes(day) && minutes >= start && minutes < end
  return (policy.days.includes(day) && minutes >= start) || (policy.days.includes(previousDay) && minutes < end)
}

// ─── Politika ───────────────────────────────────────────────────────────────

/** DB satırından politika; kolonlar yoksa (migration öncesi) kısıt yok sayılır. */
export function accessPolicyFromRow(row: AccessPolicyRow | null | undefined): AccessPolicy {
  if (!row) return NO_ACCESS_POLICY
  const days = Array.isArray(row.access_days) ? row.access_days.map(Number) : null
  const toTime = (v: string | null | undefined) => {
    const m = timeToMinutes(v)
    return m === null ? (v ?? null) : minutesToTime(m)
  }
  const ips = Array.isArray(row.access_allowed_ips) ? row.access_allowed_ips.map(String) : null
  return {
    days,
    timeStart: toTime(row.access_time_start),
    timeEnd: toTime(row.access_time_end),
    ips,
  }
}

export function hasAccessPolicy(policy: AccessPolicy): boolean {
  return policy.days !== null || policy.ips !== null
}

/**
 * Member için erişim kararı; izinliyse null. IP kısıtında istemci IP'si yalnız
 * edge'in doğruladığı cf-connecting-ip'den gelir; yoksa/bozuksa erişim kapalıdır.
 */
export function evaluateAccess(policy: AccessPolicy, ctx: { ip: string; now: Date }): AccessDenial | null {
  if (policy.ips !== null && !ipMatchesAny(ctx.ip, policy.ips)) return 'erisim-ip'
  if (!isWithinSchedule(policy, ctx.now)) return 'erisim-saat'
  return null
}

/**
 * Formdan gelen politikayı doğrular ve kanonikleştirir (server action'da son karar).
 * Tüm günler + tüm gün = kısıt yok; boş liste hatadır (kapatmak için null gönderilir).
 */
export function validateAccessPolicyInput(input: unknown): AccessPolicy {
  if (!input || typeof input !== 'object') throw new Error('Geçersiz erişim kısıtı')
  const raw = input as Record<string, unknown>

  let days: number[] | null = null
  if (raw.days !== null && raw.days !== undefined) {
    if (!Array.isArray(raw.days)) throw new Error('Geçersiz gün listesi')
    if (raw.days.some((d) => !Number.isInteger(d) || (d as number) < 1 || (d as number) > 7)) {
      throw new Error('Geçersiz gün')
    }
    days = [...new Set(raw.days as number[])].sort((a, b) => a - b)
    if (days.length === 0) throw new Error('Gün ve saat kısıtı için en az bir gün seçin')
  }

  const start = raw.timeStart ?? null
  const end = raw.timeEnd ?? null
  let timeStart: string | null = null
  let timeEnd: string | null = null
  if (start !== null || end !== null) {
    if (days === null) throw new Error('Saat aralığı için gün seçin')
    const startMin = typeof start === 'string' ? timeToMinutes(start) : null
    const endMin = typeof end === 'string' ? timeToMinutes(end) : null
    if (startMin === null || endMin === null) throw new Error('Başlangıç ve bitiş saatini SS:DD biçiminde girin')
    if (startMin === endMin) throw new Error('Başlangıç ve bitiş saati aynı olamaz')
    timeStart = minutesToTime(startMin)
    timeEnd = minutesToTime(endMin)
  }
  // Her gün + tüm gün hiçbir şeyi kısıtlamaz.
  if (days?.length === 7 && timeStart === null) days = null

  let ips: string[] | null = null
  if (raw.ips !== null && raw.ips !== undefined) {
    if (!Array.isArray(raw.ips)) throw new Error('Geçersiz IP listesi')
    const normalized: string[] = []
    for (const entry of raw.ips) {
      if (typeof entry !== 'string' || !entry.trim()) continue
      const value = normalizeIpEntry(entry)
      if (!value) throw new Error(`Geçersiz IP adresi: "${entry.trim().slice(0, 60)}"`)
      if (!normalized.includes(value)) normalized.push(value)
    }
    if (normalized.length === 0) throw new Error('IP kısıtı için en az bir IP adresi girin')
    if (normalized.length > MAX_ACCESS_IPS) throw new Error(`En fazla ${MAX_ACCESS_IPS} IP adresi girilebilir`)
    ips = normalized
  }

  return { days, timeStart, timeEnd, ips }
}

/** Günleri kısaltır: [1..5] → "Pzt–Cum", [1,3,5] → "Pzt, Çar, Cum", 7 gün → "Her gün". */
export function formatDays(days: readonly number[]): string {
  const sorted = [...new Set(days)].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b)
  if (sorted.length === 7) return 'Her gün'
  const runs: number[][] = []
  for (const d of sorted) {
    const last = runs[runs.length - 1]
    if (last && last[last.length - 1] === d - 1) last.push(d)
    else runs.push([d])
  }
  return runs
    .flatMap((run) => run.length >= 3
      ? [`${DAY_LABELS[run[0]! - 1]}–${DAY_LABELS[run[run.length - 1]! - 1]}`]
      : run.map((d) => DAY_LABELS[d - 1]!))
    .join(', ')
}

/** Liste ve mesajlarda kısa özet; kısıt yoksa boş dizi. */
export function summarizeAccessPolicy(policy: AccessPolicy): string[] {
  const parts: string[] = []
  if (policy.days !== null) {
    const hours = policy.timeStart && policy.timeEnd ? `${policy.timeStart}–${policy.timeEnd}` : 'tüm gün'
    parts.push(`${formatDays(policy.days)} ${hours}`)
  }
  if (policy.ips !== null) parts.push(policy.ips.length === 1 ? policy.ips[0]! : `${policy.ips.length} IP`)
  return parts
}
